import { ColDef, NewValueParams } from '@ag-grid-community/core';
import { DbSaveService } from '@data';
import { ActiveStatusEnum, Guideline } from '@models';

import { AgFns } from '../components/ag-grid/ag-fns';

/** The level of an override. The chain is always master -> supplier -> account -> program. */
export type GuidelineLevel = 'supplier' | 'account' | 'program';

/** The one scope a page makes overrides for. A program also names its account, for the level above it. */
export interface GuidelineScope {
  level: GuidelineLevel;
  id: string;
  accountId?: string;
}

/** One grid row: one master. Its overrides are read from master.overrides, so undo and save need no rebuild. */
export interface GuidelineOverrideRow {
  master: Guideline;
}

const STATUS_NAMES: Record<number, string> = { [ActiveStatusEnum.Active]: 'Active', [ActiveStatusEnum.Inactive]: 'Inactive' };

/**
 * The AI Guidelines grid of one scope: the Program form tab, and the Account and Supplier pages.
 *
 * Nobody here writes a new rule. Each row is a master from Utilities > AI Guidelines, and this
 * page can only override it, once, for its own scope. An override always points to the master;
 * the order comes from the level (master -> supplier -> account -> program). An override AMENDS
 * the level above it: the AI reads the whole chain and the narrowest level wins where they differ.
 */
export class GuidelineOverrideGrid {
  rows: GuidelineOverrideRow[] = [];

  constructor(
    private dbSaveService: DbSaveService,
    private scope: () => GuidelineScope,
    private isReadOnly: () => boolean,
  ) {}

  /** One row per master, from the guidelines an endpoint returned (masters and overrides). */
  setGuidelines(guidelines: Guideline[]) {
    this.rows = guidelines
      .filter((g) => !g.masterGuidelineId)
      .sort((a, b) => GuidelineOverrideGrid.ruleName(a).localeCompare(GuidelineOverrideGrid.ruleName(b)))
      .map((master) => ({ master }));
  }

  /** The name a rule shows in grids and messages. */
  static ruleName(g: Guideline): string {
    if (g.title?.trim()) return g.title.trim();
    const text = (g.instruction ?? '').trim();
    return text ? (text.length > 60 ? text.slice(0, 57) + '...' : text) : '(new rule)';
  }

  /** This scope's override of the row's master, if there is one. */
  own(row: GuidelineOverrideRow): Guideline | undefined {
    const s = this.scope();
    return this.overridesOf(row).find((o) => this.idAt(o, s.level) === s.id);
  }

  /** The overrides above this scope: supplier overrides, then (for a program) its account's override. */
  above(row: GuidelineOverrideRow): Guideline[] {
    const s = this.scope();
    if (s.level === 'supplier') return [];
    const suppliers = this.overridesOf(row).filter((o) => !!o.supplierId);
    const account = s.level === 'program' ? this.overridesOf(row).filter((o) => o.accountId && o.accountId === s.accountId) : [];
    return [...suppliers, ...account];
  }

  /** On an account page: the overrides of the account's programs, which sit under its override. */
  below(row: GuidelineOverrideRow): Guideline[] {
    const s = this.scope();
    if (s.level !== 'account') return [];
    return this.overridesOf(row).filter((o) => o.programId && o.program?.accountId === s.id);
  }

  colDefs(): ColDef<GuidelineOverrideRow>[] {
    const level = this.scope().level;
    // An edit makes, changes or retires the override, so Status and In Effect change with it.
    const refreshRow = (p: NewValueParams<GuidelineOverrideRow>) => p.api.refreshCells({ rowNodes: [p.node!], force: true });
    const ownEditable = (row: GuidelineOverrideRow) =>
      !this.isReadOnly() && row.master.activeStatusId !== ActiveStatusEnum.Inactive;
    const cols: ColDef<GuidelineOverrideRow>[] = [
      {
        headerName: 'Master',
        colId: 'master',
        valueGetter: (p) => (p.data ? GuidelineOverrideGrid.ruleName(p.data.master) : ''),
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(180),
      },
      {
        headerName: 'Master Instruction',
        colId: 'masterInstruction',
        valueGetter: (p) => p.data?.master.instruction ?? '',
        tooltipValueGetter: (p) => p.data?.master.instruction ?? '',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(380),
      },
    ];
    if (level !== 'supplier') {
      cols.push({
        headerName: 'Overrides Above',
        colId: 'above',
        valueGetter: (p) => (p.data ? this.describe(this.above(p.data)) : ''),
        tooltipValueGetter: (p) => (p.data ? this.describe(this.above(p.data)) : ''),
        ...AgFns.flexWidth(240),
      });
    }
    cols.push(
      {
        headerName: `${this.levelName(level)} Override`,
        colId: 'own',
        editable: (p) => !!p.data && ownEditable(p.data),
        valueGetter: (p) => (p.data ? (this.own(p.data)?.instruction ?? '') : ''),
        valueSetter: (p) => this.setOwnText(p.data, (p.newValue ?? '') as string),
        tooltipValueGetter: (p) => (p.data ? (this.own(p.data)?.instruction ?? '') : ''),
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: { maxLength: 8000, rows: 12, cols: 80 },
        onCellValueChanged: refreshRow,
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(380),
      },
      {
        headerName: 'Status',
        colId: 'ownStatus',
        editable: (p) => !!p.data && ownEditable(p.data) && !!this.own(p.data),
        valueGetter: (p) => {
          const own = p.data ? this.own(p.data) : undefined;
          return own ? STATUS_NAMES[own.activeStatusId] : '';
        },
        valueSetter: (p) => {
          const own = this.own(p.data);
          if (!own) return false;
          own.activeStatusId = p.newValue === 'Inactive' ? ActiveStatusEnum.Inactive : ActiveStatusEnum.Active;
          return true;
        },
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: { values: ['Active', 'Inactive'] },
        onCellValueChanged: refreshRow,
        ...AgFns.fixWidth(100),
      },
    );
    if (level === 'account') {
      cols.push({
        headerName: 'Program Overrides Below',
        colId: 'below',
        valueGetter: (p) => (p.data ? this.describe(this.below(p.data)) : ''),
        tooltipValueGetter: (p) => (p.data ? this.describe(this.below(p.data)) : ''),
        ...AgFns.flexWidth(200),
      });
    }
    cols.push({
      headerName: 'In Effect',
      colId: 'inEffect',
      valueGetter: (p) => (p.data ? this.inEffect(p.data) : ''),
      tooltipValueGetter: (p) => (p.data ? this.inEffect(p.data) : ''),
      ...AgFns.flexWidth(180),
    });
    return cols;
  }

  /** What the AI reads for this scope: the master, then each active override down to this one. */
  inEffect(row: GuidelineOverrideRow): string {
    if (row.master.activeStatusId === ActiveStatusEnum.Inactive) return 'No - the master is Inactive';
    const own = this.own(row);
    const chain = [...this.above(row), ...(own ? [own] : [])]
      .filter((o) => o.activeStatusId !== ActiveStatusEnum.Inactive)
      .map((o) => this.levelLabel(o));
    return ['Master', ...chain].join(' + ');
  }

  /** What stops a save. Empty = it may save. */
  problems(): string[] {
    const out: string[] = [];
    for (const row of this.rows) {
      const own = this.own(row);
      if (own && !own.entityAspect.entityState.isUnchanged() && !(own.instruction ?? '').trim()) {
        out.push(`${GuidelineOverrideGrid.ruleName(row.master)}: the override needs an instruction. To retire it, set its Status to Inactive.`);
      }
    }
    return out;
  }

  /**
   * Account page only: the changed account overrides that have program overrides under them.
   * A warning, not a refusal: the admin checks those program overrides, and the save continues.
   */
  warnings(): string[] {
    const out: string[] = [];
    for (const row of this.rows) {
      const own = this.own(row);
      if (!own || own.entityAspect.entityState.isUnchanged()) continue;
      const below = this.below(row).filter((o) => o.activeStatusId !== ActiveStatusEnum.Inactive);
      if (below.length) {
        const names = below.map((o) => o.program?.name ?? '?').join(', ');
        out.push(
          `${GuidelineOverrideGrid.ruleName(row.master)}: ${below.length} program override(s) sit under this override (${names}). Check them.`,
        );
      }
    }
    return out;
  }

  /** Typing text makes this scope's override; clearing the text of an unsaved one removes it. */
  private setOwnText(row: GuidelineOverrideRow, text: string): boolean {
    const s = this.scope();
    const own = this.own(row);
    const trimmed = text.trim();
    if (!own) {
      if (!trimmed) return false;
      this.dbSaveService.createEntity(Guideline, {
        guidelineContextId: row.master.guidelineContextId,
        masterGuidelineId: row.master.id,
        [`${s.level}Id`]: s.id,
        instruction: trimmed,
        activeStatusId: ActiveStatusEnum.Active,
      } as Partial<Guideline>);
      return true;
    }
    if (!trimmed && own.entityAspect.entityState.isAdded()) {
      own.entityAspect.setDetached();
      return true;
    }
    own.instruction = trimmed;
    return true;
  }

  private overridesOf(row: GuidelineOverrideRow): Guideline[] {
    return (row.master.overrides ?? []).filter((o) => !o.entityAspect.entityState.isDetached());
  }

  private idAt(o: Guideline, level: GuidelineLevel): string | undefined {
    return level === 'supplier' ? o.supplierId : level === 'account' ? o.accountId : o.programId;
  }

  private levelName(level: GuidelineLevel) {
    return level === 'supplier' ? 'Supplier' : level === 'account' ? 'Account' : 'Program';
  }

  private levelLabel(o: Guideline): string {
    if (o.supplierId) return `Supplier ${o.supplier?.name ?? ''}`.trim();
    if (o.accountId) return 'Account';
    return o.program?.name ? `Program ${o.program.name}` : 'Program';
  }

  private describe(overrides: Guideline[]): string {
    return overrides
      .map((o) => `${this.levelLabel(o)}${o.activeStatusId === ActiveStatusEnum.Inactive ? ' (Inactive)' : ''}: ${o.instruction}`)
      .join(' | ');
  }
}
